import { useEffect, type Dispatch } from 'react'
import { getDraft, saveDraft } from '@/lib/storage/local-storage'
import type { SecurityOptions, PasscodeOptions, FormatOptions } from '@/types/share'
import type { EditorState } from '../state'
import type { EditorAction } from '../state'

interface UseShareOptionsSyncOptions {
  isShareOpen: boolean
  defaultSecurityOptions: SecurityOptions
  defaultPasscodeOptions: PasscodeOptions
  defaultFormatOptions: FormatOptions
}

/**
 * Syncs share options between the Share modal and the current draft.
 * - When the modal opens, loads options from the draft or falls back to defaults
 * - While the modal is open, persists option changes back to the draft
 */
export function useShareOptionsSync(
  state: EditorState,
  dispatch: Dispatch<EditorAction>,
  options: UseShareOptionsSyncOptions,
) {
  const { isShareOpen, defaultSecurityOptions, defaultPasscodeOptions, defaultFormatOptions } = options

  // Load share settings when Share modal opens
  useEffect(() => {
    if (isShareOpen) {
      if (state.currentDraftId) {
        const draft = getDraft(state.currentDraftId)
        if (draft?.securityOptions && draft?.passcodeOptions) {
          dispatch({
            type: 'LOAD_SHARE_OPTIONS',
            security: draft.securityOptions,
            passcode: draft.passcodeOptions,
            format: draft.formatOptions ?? defaultFormatOptions,
          })
          return
        }
      }
      dispatch({
        type: 'RESET_SHARE_OPTIONS',
        security: defaultSecurityOptions,
        passcode: defaultPasscodeOptions,
        format: defaultFormatOptions,
      })
    }
  }, [isShareOpen, state.currentDraftId, defaultSecurityOptions, defaultPasscodeOptions, defaultFormatOptions, dispatch])

  // Save share options to current draft when they change
  useEffect(() => {
    if (state.currentDraftId && isShareOpen) {
      const draft = getDraft(state.currentDraftId)
      if (draft) {
        saveDraft({
          ...draft,
          securityOptions: state.securityOptions,
          passcodeOptions: state.passcodeOptions,
          formatOptions: state.formatOptions,
        })
      }
    }
  }, [state.currentDraftId, state.securityOptions, state.passcodeOptions, state.formatOptions, isShareOpen])
}
